import { usePopupManager } from 'react-popup-manager';
import { useCallback } from 'react';
import { PredefinedComment } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/predefined-comments.model';
import { usePredefinedCommentsList } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-list/hooks/use-predefined-comments-list.hook';
import { updatePredefinedCommentActiveStatus } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/service/predefined-comments.service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { useTranslation } from 'core/i18n';
import { PredefinedCommentsActiveConfirmationModalComponent } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-list/components/predefined-comments-active-confirmation-modal/predefined-comments-active-confirmation-modal.component';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { executeRequest } from 'core/utils/backend-service';

type PredefinedCommentModificationType = 'activate' | 'deactivate';

export const useActivateDeactivatePredefinedComment = () => {
    const popupManager = usePopupManager();
    const { t } = useTranslation();
    const { refreshList } = usePredefinedCommentsList();

    const { mutateAsync: updateActiveStatus } = usePocMutation({
        mutationFn: ({ ids, isEnabled }: { ids: string[]; isEnabled: boolean }) =>
            executeRequest(() => updatePredefinedCommentActiveStatus(ids, isEnabled)),
    });

    const onActivationChange = useCallback(
        async (ids: string[], modificationType: PredefinedCommentModificationType) => {
            const isEnabled = modificationType === 'activate';
            await updateActiveStatus({ ids, isEnabled }).then(() => {
                showSuccessNotification(
                    t(
                        isEnabled
                            ? 'settings.devices.predefined-comments.list.actions.activate.notification.success'
                            : 'settings.devices.predefined-comments.list.actions.deactivate.notification.success'
                    )
                );
                refreshList();
            });
        },
        [refreshList, t, updateActiveStatus]
    );

    const showDeactivateModal = useCallback(
        (predefinedComments: PredefinedComment[]) => {
            popupManager.open(PredefinedCommentsActiveConfirmationModalComponent, {
                actionFn: (ids: string[]) => onActivationChange(ids, 'deactivate'),
                predefinedComments,
                action: 'deactivate',
            });
        },
        [popupManager, onActivationChange]
    );

    const showActivateModal = useCallback(
        (predefinedComments: PredefinedComment[]) => {
            popupManager.open(PredefinedCommentsActiveConfirmationModalComponent, {
                actionFn: (ids: string[]) => onActivationChange(ids, 'activate'),
                predefinedComments,
                action: 'activate',
            });
        },
        [popupManager, onActivationChange]
    );

    return {
        showDeactivateModal,
        showActivateModal,
        onActivationChange,
    };
};
